import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AppButton, AppIconButton } from '../../design-system';
import { getTableSelectionColors } from '../../../theme/uiSemanticColors';
import { Close as CloseIcon } from '@mui/icons-material';
import type { BulkAction } from './types';

interface BulkActionBarProps {
  selectedCount: number;
  bulkActions: BulkAction[];
  selectedIds: (string | number)[];
  onClearSelection: () => void;
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  bulkActions,
  selectedIds,
  onClearSelection,
}) => {
  const theme = useTheme();
  const tableSelection = useMemo(() => getTableSelectionColors(theme), [theme]);
  const [confirmAction, setConfirmAction] = useState<BulkAction | null>(null);

  const handleActionClick = (action: BulkAction) => {
    if (action.confirmMessage) {
      setConfirmAction(action);
    } else {
      action.onClick(selectedIds);
      onClearSelection();
    }
  };

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction.onClick(selectedIds);
      setConfirmAction(null);
      onClearSelection();
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          mb: 2,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 1,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body1" fontWeight="medium">
            {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
          </Typography>

          <Stack direction="row" spacing={1}>
            {bulkActions.map((action, index) => (
              <AppButton
                key={index}
                size="small"
                startIcon={action.icon}
                onClick={() => handleActionClick(action)}
                sx={{
                  color: 'primary.contrastText',
                  borderColor: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: tableSelection.bulkActionHoverBg,
                  },
                }}
                variant="outlined"
              >
                {action.label}
              </AppButton>
            ))}
          </Stack>
        </Stack>

        <AppIconButton
          size="small"
          onClick={onClearSelection}
          sx={{ color: 'primary.contrastText' }}
        >
          <CloseIcon />
        </AppIconButton>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={Boolean(confirmAction)} onClose={() => setConfirmAction(null)}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmAction?.confirmMessage?.replace('{count}', String(selectedCount))}
          </Typography>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setConfirmAction(null)}>Cancel</AppButton>
          <AppButton onClick={handleConfirm} variant="contained" color="primary">
            Confirm
          </AppButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkActionBar;
